import type { CreateTournamentRequest } from '@brush/api-client';
import { useState } from 'react';
import { btnGhost, btnPrimary, Modal } from '../Modal';
import {
  draftIsValid,
  newDraft,
  TournamentFields,
  type TournamentDraft,
} from './TournamentFields';

export function CreateTournamentModal({
  roomId,
  allTags,
  platformTags,
  defaultTags,
  today,
  saving,
  onSave,
  onClose,
}: {
  roomId: string;
  allTags: string[];
  platformTags: string[];
  defaultTags: string[];
  /** The room's gaming day (ADR 0014), which a new tournament is dated by default. */
  today: string;
  saving: boolean;
  onSave: (body: CreateTournamentRequest) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<TournamentDraft>(() => newDraft(defaultTags, today));

  const valid = draftIsValid(draft);

  // Blank optional strings become null on the way out: a value that exists but says nothing
  // would still render as one wherever the tournament is shown.
  const submit = () =>
    onSave({
      roomId,
      internalId: draft.internalId.trim(),
      name: draft.name.trim(),
      date: draft.date,
      time: draft.time,
      buyIn: Number(draft.buyIn),
      guarantee: draft.guarantee,
      guaranteeUnits: draft.guaranteeUnits.trim() || null,
      eventNumber: draft.eventNumber.trim() || null,
      isMultiDay: draft.isMultiDay,
      sessions: draft.sessions,
      status: draft.status,
      tags: draft.tags,
    });

  return (
    <Modal
      title="New tournament"
      subtitle="Results can be uploaded separately once the tournament finishes."
      onClose={onClose}
      width="max-w-2xl"
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!valid || saving}
            className={btnPrimary}
          >
            {saving ? 'Creating…' : 'Create tournament'}
          </button>
        </>
      }
    >
      <TournamentFields
        draft={draft}
        onChange={setDraft}
        allTags={allTags}
        platformTags={platformTags}
        defaultTagsNote={defaultTags.length > 0}
        showStatus
      />

      {/* Says which field is holding the button, rather than leaving a disabled button to be
          guessed at. Both are required and neither is the first thing you fill in. */}
      {!valid ? (
        <p className="mt-4 text-xs text-ink-800/50">
          A tournament name and an internal ID are required.
        </p>
      ) : null}
    </Modal>
  );
}
